import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import moment from 'moment'
import router from '../router'
import settings from '../constants/settings.js'

Vue.use(Vuex)

const uri = settings.URL_API
const user = JSON.parse(localStorage.getItem('user'))
import authHeader from '../services/authHeader';

const initialState = user
    ? { status: { loggedIn: true }, user }
    : { status: { loggedIn: false }, user: null };

function createDashboardWeeklyAverageState(overrides = {}) {
    return {
        status: 'idle',
        value: null,
        hourMinute: null,
        sensorName: null,
        referenceTs: null,
        updatedAt: null,
        requestKey: null,
        ...overrides
    };
}

const PA_TREND_OFFSET_DEVICE_ID = 'device038'
const CHILE_TIMEZONE = 'America/Santiago'

function subtractTwoFromTrendValue(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value - 2
    }

    if (typeof value !== 'string') {
        return value
    }

    const trimmedValue = value.trim()
    if (trimmedValue.length === 0) {
        return value
    }

    if (trimmedValue.includes(',')) {
        const parts = trimmedValue.split(',').map(part => part.trim())
        const parsedParts = parts.map(part => Number(part))

        if (parsedParts.some(part => !Number.isFinite(part))) {
            return value
        }

        return parsedParts.map(part => String(part - 2)).join(', ')
    }

    const numericValue = Number(trimmedValue)
    if (!Number.isFinite(numericValue)) {
        return value
    }

    return String(numericValue - 2)
}

function adjustPaTrendResponse(payload, deviceId) {
    if (deviceId !== PA_TREND_OFFSET_DEVICE_ID) {
        return payload
    }

    if (typeof payload !== 'object' || payload === null) {
        return payload
    }

    const message = payload.message
    if (!message) {
        return payload
    }

    const dayStatsRange = Array.isArray(message.dayStatsRange)
        ? message.dayStatsRange.map(dayEntry => ({
            ...dayEntry,
            grupos: Array.isArray(dayEntry.grupos)
                ? dayEntry.grupos.map(groupEntry => ({
                    ...groupEntry,
                    mode: subtractTwoFromTrendValue(groupEntry.mode)
                }))
                : dayEntry.grupos
        }))
        : message.dayStatsRange

    const dailyStatsRangeProcess = Array.isArray(message.dailyStatsRangeProcess)
        ? message.dailyStatsRangeProcess.map(processEntry => ({
            ...processEntry,
            moda: subtractTwoFromTrendValue(processEntry.moda)
        }))
        : message.dailyStatsRangeProcess

    return {
        ...payload,
        message: {
            ...message,
            dayStatsRange,
            dailyStatsRangeProcess
        }
    }
}

function getChileUtcOffset(dateValue) {
    const parsedDate = moment(dateValue, [
        moment.ISO_8601,
        'YYYY-MM-DD',
        'DD-MM-YYYY'
    ], true)

    const referenceMoment = parsedDate.isValid() ? parsedDate : moment(dateValue)

    if (!referenceMoment.isValid()) {
        return '-03:00'
    }

    const referenceDate = new Date(Date.UTC(
        referenceMoment.year(),
        referenceMoment.month(),
        referenceMoment.date(),
        12,
        0,
        0
    ))

    try {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: CHILE_TIMEZONE,
            timeZoneName: 'longOffset'
        })

        const parts = formatter.formatToParts(referenceDate)
        const timezonePart = parts.find(part => part.type === 'timeZoneName')

        if (!timezonePart || typeof timezonePart.value !== 'string') {
            return '-03:00'
        }

        const match = timezonePart.value.match(/GMT([+-]\d{2}:\d{2})/)
        return match ? match[1] : '-03:00'
    } catch (error) {
        return '-03:00'
    }
}

export const store = new Vuex.Store({
    state: {
        drawer: true,
        user: initialState.user,
        status: initialState.status,
        loadLogin: false,
        loadRegister: false,
        errorLogin: "",
        errorRegister: "",
        errorRecover: "",
        successRecover: false,
        successRegister: false,
        snackbar: {
            view: false,
            text: '',
            color: 'success'
        },
        isLoading: false,
        devices: [],
        sensors: [],
        allSensors: [],
        optionsDevices: [],
        devicesInfo: [],
        devicesLastReport: [],
        devicesDateReport: null,
        devicesDateReportOriginal: null,
        devicesDateTrendReport: null,
        devicesDateTrendStats: null,
        devicesDateResumeReport: null,
        loadingReportData: false,
        devicesDateEvent: null,
        devicesDateCompare: null,
        downloadFiles: [],
        downloadUrl: '',
        loadDownloadFiles: false,
        loadDownloadUrl: false,
        windRose: [],
        classificationEvents: [],
        viewDate: moment().format('YYYY-MM-DD'),
        viewRangeDate: null,
        operation: {},
        idOperation: null,
        annotationTag: [],
        annotation: [],
        projects: [],
        projectView: 'all',
        models: [],
        projectTest: [],
        tmpProjectReports: [],
        viewProjectTest: {},
        reportReferences: {},
        dashBoardDevices: {},
        dashboardWeeklyAverages: {},
        dashboardCarouselRuntime: {
            routeActive: false,
            autoMode: false,
            slideIndex: 0,
            slideType: null,
            activeDeviceId: null,
            progressPct: 0,
            slideDurationMs: 20000,
            totalSlides: 0,
            isDev: false,
            updatedAt: null
        },
        dashboardCarouselCommand: {
            seq: 0,
            command: null,
            source: null,
            payload: null,
            issuedAt: null
        },
        rangeReportData: undefined,
        rangeTrend: [],
        deviceStatsTrend: {}
    },

    mutations: {
        toggleDrawer(state, payload) {
            state.drawer = payload
        },
        loginSuccess(state, payload) {
            state.status.loggedIn = true
            state.user = payload
            state.loadLogin = false
        },
        loginFailure(state, payload) {
            state.status.loggedIn = false
            state.user = null
            state.loadLogin = false
            state.errorLogin = payload
        },
        loginInit(state) {
            state.loadLogin = true
        },
        logout(state) {
            state.status.loggedIn = false
            state.user = null
        },
        registerInit(state) {
            state.loadRegister = true
            state.successRegister = false
        },
        registerSuccess(state) {
            state.loadRegister = false
            state.status.loggedIn = false
            state.successRegister = true
        },
        registerFailure(state, payload) {
            state.loadRegister = false
            state.status.loggedIn = false
            state.errorRegister = payload
        },
        recoverInit(state) {
            state.successRecover = false
        },
        recoverSuccess(state) {
            state.successRecover = true
        },
        recoverFailure(state) {
            state.errorRecover = true
            state.successRecover = false
        },
        setLoading(state, payload) {
            state.isLoading = payload
        },
        setNotification(state, payload) {
            state.snackbar = payload
        },
        pullDevices(state, payload) {
            state.devices = payload
        },
        pullSensors(state, payload) {
            state.sensors = payload
        },
        pullAllSensors(state, payload) {
            state.allSensors = payload
        },
        pullOptionsDevices(state, payload) {
            state.optionsDevices = payload
        },
        pullOptionsDevice(state, payload) {
            state.optionsDevices.forEach(element => {
                if (element._id === payload._id) {
                    element = payload
                    return
                }
            })
        },
        pullDevicesInfo(state, payload) {
            state.devicesLastReport = payload
        },
        updateDeviceInfo(state, deviceInfo) {
            // Asegurar que devicesInfo sea un array
            if (!Array.isArray(state.devicesInfo)) {
                state.devicesInfo = []
            }

            // Buscar si el dispositivo ya existe en devicesInfo
            const index = state.devicesInfo.findIndex(d => d.deviceId === deviceInfo.deviceId)

            if (index !== -1) {
                // Actualizar dispositivo existente usando Vue.set para reactividad
                state.devicesInfo.splice(index, 1, deviceInfo)
            } else {
                // Agregar nuevo dispositivo
                state.devicesInfo.push(deviceInfo)
            }
        },
        setDeviceDateReport(state, payload) {
            state.devicesDateReport = payload
        },
        setDeviceDateReportOriginal(state, payload) {
            state.devicesDateReportOriginal = payload
        },
        setDeviceDateTrendReport(state, payload) {
            state.devicesDateTrendReport = payload
        },
        setdeviceStatsTrend(state, payload) {
            state.deviceStatsTrend = payload
        },
        loadingReportData(state, payload) {
            state.loadingReportData = payload
        },
        setDeviceDateResumeReport(state, payload) {
            state.devicesDateResumeReport = payload
        },
        setDeviceDateEvent(state, payload) {
            state.devicesDateEvent = payload
        },
        setDownloadFiles(state, payload) {
            state.downloadFiles = payload
        },
        setDownloadUrl(state, payload) {
            state.downloadUrl = payload
        },
        loadDownloadFiles(state, payload) {
            state.loadDownloadFiles = payload
        },
        loadDownloadUrl(state, payload) {
            state.loadDownloadUrl = payload
        },
        setWindRose(state, payload) {
            state.windRose = payload
        },
        setClassificationEvents(state, payload) {
            state.classificationEvents = payload
        },
        setDevicesDateCompare(state, payload) {
            state.devicesDateCompare = payload
        },
        updateDevice(state, payload) {
            state.devices.forEach(element => {
                if (element.deviceId === payload.deviceId) {
                    element = payload
                    return
                }
            })

            // TODO: agregar deviceinfo...
        },
        updateOptionsDevices(state, payload) {
            state.optionsDevices.forEach(element => {
                if (element.deviceId === payload.deviceId) {
                    element = payload
                    return
                }
            })
        },
        removeDevice(state, payload) {
            state.devices = state.devices.filter(e => {
                return e.deviceId != payload
            })

            state.devicesInfo = state.devices.filter(e => {
                return e.deviceId != payload
            })
        },
        updateSensor(state, payload) {
            state.sensors.forEach(element => {
                if (element._id === payload._id) {
                    element = payload
                    return
                }
            })
        },
        setViewDate(state, payload) {
            state.viewDate = payload
        },
        setViewRangeDate(state, payload) {
            state.viewRangeDate = payload
        },
        setOperation(state, payload) {
            state.operation = payload
        },
        idOperation(state, payload) {
            state.idOperation = payload
        },
        setAnnotation(state, payload) {
            state.annotation = payload
        },
        setAnnotationTag(state, payload) {
            state.annotationTag = payload
        },
        addAnnotationTag(state, payload) {
            state.annotationTag.push(payload)
        },
        setProjects(state, payload) {
            state.projects = payload
        },
        setProject(state, payload) {
            state.projectView = payload
        },
        addProject(state, payload) {
            state.projects.push(payload)
        },
        setModels(state, payload) {
            state.models = payload
        },
        addModel(state, payload) {
            state.models.push(payload)
        },

        setProjectTest(state, payload) {
            state.projectTest = payload
        },

        setViewProjectTest(state, payload) {
            state.viewProjectTest = state.projectTest.find(e => e._id === payload)
        },

        addProjectTest(state, payload) {
            state.projectTest.push(payload)
        },

        editProjectTest(state, payload) {
            const index = state.projectTest.indexOf(e => e._id === payload._id)
            state.projectTest[index] = payload
        },

        tempUpdateReports(state, payload) {
            state.tmpProjectReports = state.tmpProjectReports.concat(payload)
        },

        cleanAnnotationTag(state, payload) {
            for (let i = 0; i < state.annotationTag.length; i++) {
                const element = state.annotationTag[i]

                if (element._id === payload.id) {
                    state.annotationTag.splice(i, 1)
                    return
                }
            }
        },

        setReportReferences(state, payload) {
            const { deviceId, reference } = payload
            state.reportReferences[deviceId] = reference
        },

        setDashboardData(state, payload) {
            const { deviceId, info } = payload
            if (deviceId === null) {
                // Limpiar todos los datos del dashboard
                state.dashBoardDevices = {}
            } else {
                state.dashBoardDevices[deviceId] = info
            }
        },

        setDashboardWeeklyAverage(state, payload) {
            const { deviceId, ...entry } = payload

            if (!deviceId) return

            const current = state.dashboardWeeklyAverages[deviceId] || createDashboardWeeklyAverageState()
            Vue.set(state.dashboardWeeklyAverages, deviceId, {
                ...current,
                ...entry
            })
        },

        clearDashboardWeeklyAverages(state) {
            state.dashboardWeeklyAverages = {}
        },

        setDashboardCarouselRuntime(state, payload) {
            state.dashboardCarouselRuntime = {
                ...state.dashboardCarouselRuntime,
                ...payload,
                updatedAt: Date.now()
            }
        },

        emitDashboardCarouselCommand(state, payload) {
            const currentSeq = state.dashboardCarouselCommand && state.dashboardCarouselCommand.seq
                ? state.dashboardCarouselCommand.seq
                : 0

            state.dashboardCarouselCommand = {
                seq: currentSeq + 1,
                command: payload.command,
                source: payload.source || null,
                payload: payload.payload || null,
                issuedAt: Date.now()
            }
        },

        rangeReportData(state, payload) {
            state.rangeReportData = payload
        },

        setRangeTrend(state, payload) {
            state.rangeTrend = payload
        }
    },

    actions: {
        toggleDrawer({ commit }, payload) {
            commit('toggleDrawer', payload)
        },
        login({ commit }, payload) {
            const url = `${uri}/auth/signin`
            const authData = {
                username: payload.username,
                password: payload.password
            }

            commit('loginInit')

            axios.post(url, authData, { headers: authHeader() })
                .then(response => {
                    if (response.data.accessToken) {
                        response.data["remember"] = payload.remember
                        localStorage.setItem('user', JSON.stringify(response.data))
                        commit('loginSuccess', response.data)
                    }
                })
                .catch(error => {
                    commit('loginFailure', error.response.data.message)
                    console.log(error)
                })
        },
        logout({ commit }) {
            localStorage.removeItem('user')
            commit('logout')
        },
        register({ commit }, payload) {
            const url = `${uri}/user/signup`
            const register = {
                username: payload.username,
                password: payload.password,
            }

            commit('registerInit')

            axios.post(url, register, { headers: { 'x-access-token': payload.token } })
                .then(response => {
                    commit('registerSuccess')
                    store.dispatch('customNotification', { text: 'Usuario creado con exito', color: 'success' })
                })
                .catch(error => {
                    commit('registerFailure', error.response.data.message)
                    store.dispatch('errorNotification', { msg: error.response.data.message, error: error, needAuth: false })
                    console.log(error)
                })
        },
        sendEmailRegister({ commit }, payload) {
            const url = `${uri}/user/create`
            const register = {
                email: payload.email,
                roles: payload.roles,
                projectId: payload.projectId,
            }
            commit('registerInit')

            axios.post(url, register, { headers: authHeader() })
                .then(response => {
                    store.dispatch('customNotification', { text: 'Se ha enviado el correo electronico', color: 'success' })
                    commit('registerSuccess')
                })
                .catch(error => {
                    store.dispatch('errorNotification', { msg: error.response.data.message, error: error })
                    commit('registerFailure', error.response.data.message)
                    console.log(error)
                })
        },

        async sendEmailCode({ commit }, payload) {
            const url = `${uri}/auth/code`

            const authData = {
                user: payload.email,
                code: payload.code,
                type: payload.type
            }

            try {
                const response = await axios.post(url, authData, { headers: authHeader() })
                return response.data.accessToken
            } catch (error) {
                store.dispatch('errorNotification', { msg: error.response.data.message, error: error })
                commit('registerFailure', error.response.data.message)
                return null
            }
        },

        sendEmailReSendCode({ commit }, payload) {
            const url = `${uri}/user/resendcode`
            const register = {
                email: payload.email,
                type: payload.type
            }

            commit('registerInit')

            axios.post(url, register, { headers: authHeader() })
                .then(response => {
                    store.dispatch('customNotification', { text: 'Se ha enviado el correo electronico', color: 'success' })
                    commit('registerSuccess')
                })
                .catch(error => {
                    store.dispatch('errorNotification', { msg: error.response.data.message, error: error })
                    commit('registerFailure', error.response.data.message)
                    console.log(error)
                })
        },

        recover({ commit }, payload) {
            const url = `${uri}/auth/changepass`
            const change = {
                password: payload.password,
                token: payload.token
            }

            commit('recoverInit')

            axios.post(url, change, { headers: { 'x-access-token': payload.token } })
                .then(response => {
                    commit('recoverSuccess')
                    store.dispatch('customNotification', { text: 'Contraseña actualizada', color: 'success' })
                })
                .catch(error => {
                    commit('recoverFailure', error.response.data.message)
                    store.dispatch('errorNotification', { msg: error.response.data.message, error: error, needAuth: false })
                    console.log(error)
                })
        },
        sendEmailRecover({ commit }, payload) {
            const url = `${uri}/auth/recoverpass`
            const recover = {
                user: payload.user
            }

            commit('recoverInit')

            axios.post(url, recover, { headers: authHeader() })
                .then(response => {
                    store.dispatch('customNotification', { text: 'Se ha enviado el correo electrónico', color: 'success' })
                    commit('recoverSuccess')
                })
                .catch(error => {
                    store.dispatch('errorNotification', { msg: error.response.data.message, error: error })
                    commit('recoverFailure', error.response.data.message)
                    console.log(error)
                })
        },
        setProject({ commit }, payload) {
            commit('setProject', payload.project)
            // Limpiar datos del dashboard al cambiar de proyecto
            commit('setDashboardData', { deviceId: null, info: {} })
            commit('clearDashboardWeeklyAverages')
            // Resetear estado de carga
            commit('setLoading', false)
        },
        pullDevices({ commit }) {
            const projectId = store.getters.project
            const url = `${uri}/device/all?projectId=${projectId}`

            axios.get(url, { headers: authHeader() })
                .then(function (response) {
                    commit('pullDevices', response.data)
                })
                .catch(function (error) {
                    store.dispatch('errorNotification', { msg: 'No se pueden cargar dispositivos', error: error })
                    console.log(error)
                })
        },
        pullSensors({ commit }) {
            const projectId = store.getters.project
            const url = `${uri}/sensor/userview?project=${projectId}`

            axios.get(url, { headers: authHeader() })
                .then(function (response) {
                    commit('pullSensors', response.data)
                })
                .catch(function (error) {
                    store.dispatch('errorNotification', { msg: 'No se pueden cargar los sensores', error: error })
                    console.log(error)
                })
        },
        pullAllSensors({ commit }) {
            const projectId = store.getters.project
            const url = `${uri}/sensor/all?project=${projectId}`

            axios.get(url, { headers: authHeader() })
                .then(function (response) {
                    commit('pullAllSensors', response.data)
                })
                .catch(function (error) {
                    store.dispatch('errorNotification', { msg: 'No se pueden cargar los sensores', error: error })
                    console.log(error)
                })
        },

        pullOptionsDevices({ commit }) {
            const url = `${uri}/device/options`
            console.log("🚀 ~ pullOptionsDevices ~ url:", url)
            axios.get(url, { headers: authHeader() })
                .then(function (response) {
                    console.log("🚀 ~ pullOptionsDevices ~ response:", response.data)
                    commit('pullOptionsDevices', response.data)
                })
                .catch(function (error) {
                    store.dispatch('errorNotification', { msg: 'No se pueden cargar las opciones', error: error })
                    console.log(error)
                })
        },

        pullOptionsDevice({ commit }, deviceId) {
            // const { deviceId } = payload
            const url = `${uri}/device/${deviceId}/options`
            console.log("🚀 ~ pullOptionsDevice ~ url:", url)
            axios.get(url, { headers: authHeader() })
                .then(function (response) {
                    commit('pullOptionsDevice', response.data)
                })
                .catch(function (error) {
                    store.dispatch('errorNotification', { msg: 'No se pueden cargar las opciones', error: error })
                    console.log(error)
                })
        },

        pullInfoDevices({ commit }) {
            const projectId = store.getters.project
            const url = `${uri}/report/last?project=${projectId}`

            axios.get(url, { headers: authHeader() })
                .then(function (response) {
                    commit('pullDevicesInfo', response.data)
                })
                .catch(function (error) {
                    store.dispatch('errorNotification', { msg: 'No se pueden cargar reportes', error: error })
                    console.log(error)
                })
        },

        async pullDeviceLastLaeq({ commit }, deviceId) {
            const today = moment().format('YYYY-MM-DD')
            const url = `${uri}/report/${deviceId}/laeq_001?day=${today}`

            try {
                const response = await axios.get(url, { headers: authHeader() })

                if (response.data && response.data.length > 0) {
                    // Obtener SOLO el último reporte más reciente
                    const lastReport = response.data[response.data.length - 1]

                    // Crear estructura compatible con deviceInfo
                    const deviceInfo = {
                        deviceId: deviceId,
                        ts: lastReport.ts,
                        sensors: [{
                            sensorType: 'laeq',
                            sensorName: 'laeq_001',
                            value: lastReport.report.avg
                        }]
                    }

                    // Actualizar devicesInfo con este dispositivo (reemplaza el anterior)
                    commit('updateDeviceInfo', deviceInfo)
                    return deviceInfo
                }
                return null
            } catch (error) {
                console.error(`❌ Error cargando LAeq para ${deviceId}:`, error)
                return null
            }
        },

        pullDeviceDateReport({ commit, state }, payload) {
            let url = `${uri}/report/${payload.deviceId}?day=${payload.date}`
            let { loading } = payload

            if (payload.sensor)
                url = `${uri}/report/${payload.deviceId}/${payload.sensor}?day=${payload.date}`

            if (payload.cleanCahe)
                url += '&cleancache=true'

            if (payload.endDate)
                url += `&endDate=${payload.endDate}`

            if (payload.deviceId === undefined)
                return

            commit('loadingReportData', loading)
            commit('rangeReportData', payload.endDate)

            if (!(payload.deviceId in state.reportReferences))
                store.dispatch('pullDeviceReferenceReport', payload)

            return new Promise((resolve, reject) => {
                axios.get(url, { headers: authHeader() })
                    .then(function (response) {
                        commit('setDeviceDateReport', response.data)
                        commit('loadingReportData', false)
                        resolve(response.data)
                    })
                    .catch(function (error) {
                        store.dispatch('errorNotification', { msg: 'No se pueden cargar reportes', error: error })
                        reject(error)
                    })
            })
        },

        pullDeviceDateReportOriginal({ commit }, payload) {
            let url = `${uri}/report/${payload.deviceId}/${payload.sensor}?day=${payload.date}&modo=original`

            if (payload.endDate)
                url += `&endDate=${payload.endDate}`

            return new Promise((resolve, reject) => {
                axios.get(url, { headers: authHeader() })
                    .then((response) => {
                        commit('setDeviceDateReportOriginal', response.data)
                        resolve(response.data)
                    })
                    .catch((error) => {
                        store.dispatch('errorNotification', { msg: 'No se pueden cargar datos originales', error: error })
                        reject(error)
                    })
            })
        },

        pullDeviceDateTrendReport({ commit }, payload) {
            let { deviceId, date, endDate } = payload

            let end = endDate ? endDate : date
            const url = `${uri}/report/trend/${deviceId}/laeq_001?startDate=${date}&endDate=${end}`

            axios.get(url, { headers: authHeader() })
                .then((response) => {
                    commit('setDeviceDateTrendReport', response.data)
                })
                .catch((error) => {
                    store.dispatch('errorNotification', { msg: 'No se pueden cargar datos', error: error })
                    console.log(error)
                })
        },

        async pullDeviceStatsTrend({ commit }, payload) {
            let { deviceId, startDate, endDate, sensorName } = payload

            const start = moment(startDate).format('DD-MM-YYYY')
            const end = moment(endDate).format('DD-MM-YYYY')
            const timezone = getChileUtcOffset(startDate)

            const url = `${uri}/trend/daily/stats/${deviceId}/${sensorName}?startDate=${start}&endDate=${end}&timezone=${timezone}`

            try {
                const response = await axios.get(url, { headers: authHeader() })
                const adjustedResponse = adjustPaTrendResponse(response.data, deviceId)
                commit('setdeviceStatsTrend', adjustedResponse)
                return adjustedResponse
            } catch (error) {
                store.dispatch('errorNotification', { msg: 'No se pueden cargar datos', error: error })
                console.log(error)
            }
        },

        async getDeviceRangeStats(_, payload) {
            let { deviceId, startDate, endDate, sensorName } = payload

            const start = moment(startDate).format('DD-MM-YYYY')
            const end = moment(endDate).format('DD-MM-YYYY')
            const timezone = getChileUtcOffset(startDate)

            const url = `${uri}/trend/daily/stats/${deviceId}/${sensorName}?startDate=${start}&endDate=${end}&timezone=${timezone}`

            try {
                const response = await axios.get(url, { headers: authHeader() })
                return adjustPaTrendResponse(response.data, deviceId)
            } catch (error) {
                return null
            }
        },

        pullDeviceReferenceReport({ commit }, payload) {
            const { deviceId, sensor } = payload
            const url = `${uri}/reference/${deviceId}/${sensor}`

            axios.get(url, { headers: authHeader() })
                .then((response) => {
                    commit('setReportReferences', response.data.data)
                })
        },

        pullDeviceDateReportExtremas({ commit }, payload) {
            const url = `${uri}/report/extremes/${payload.deviceId}/${payload.sensor}?day=${payload.date}`

            return new Promise((resolve, reject) => {
                axios.get(url, { headers: authHeader() })
                    .then((response) => {
                        resolve(response)
                    })
                    .catch((error) => {
                        store.dispatch('errorNotification', { msg: 'No se pueden cargar datos', error: error })
                        console.log(error)
                        reject(error)
                    })
            })

            axios.get(url, { headers: authHeader() })
                .then((response) => {
                    commit('setOperation', response.data)
                })
                .catch((error) => {
                    store.dispatch('errorNotification', { msg: 'No se pueden cargar datos', error: error })
                    console.log(error)
                })
        },
        pullDeviceDateEvents({ commit }, payload) {
            let url = `${uri}/classification/event/${payload.deviceId}?day=${payload.date}`

            if (payload.deviceId === undefined)
                return

            return new Promise((resolve, reject) => {
                axios.get(url, { headers: authHeader() })
                    .then(function (response) {
                        commit('setDeviceDateEvent', response.data)
                        resolve(response.data)
                    })
                    .catch(function (error) {
                        store.dispatch('errorNotification', { msg: 'No se pueden cargar los eventos', error: error })
                        reject(error)
                    })
            })
        },
        pullDeviceDateCompare({ commit }, payload) {
            let url = ""
            const devices = payload.devices
            let requests = []
            let reports = []

            for (let i = 0; i < devices.length; i++) {
                url = `${uri}/report/${devices[i]}/laeq_001?day=${payload.date}`
                requests.push(axios.get(url, { headers: authHeader() }))
            }

            axios.all(requests)
                .then(axios.spread((...responses) => {
                    for (let i = 0; i < responses.length; i++) {
                        reports.push({
                            deviceId: devices[i],
                            info: responses[i].data
                        })
                    }

                    commit('setDevicesDateCompare', reports)
                }))
                .catch(function (error) {
                    store.dispatch('errorNotification', { msg: 'No se pueden cargar datos', error: error })
                    console.log(error)
                })
        },
        pullDownloadDeviceFiles({ commit }, payload) {
            commit('loadDownloadFiles', true)
            const url = `${uri}/audio/download/${payload.deviceId}?day=${payload.day}`

            axios.get(url, { headers: authHeader() })
                .then(function (response) {
                    commit('setDownloadFiles', response.data)
                    commit('loadDownloadFiles', false)
                })
                .catch(function (error) {
                    commit('setDownloadFiles', [])
                    store.dispatch('errorNotification', { msg: 'No se encontraron datos de descarga', error: error })
                    commit('loadDownloadFiles', false)
                    console.log(error)
                })
        },
        getDownloadUrl({ commit }, payload) {
            commit('loadDownloadUrl', true)

            const url = `${uri}/audio/urlfile?file=${payload.key}`

            axios.get(url, { headers: authHeader() })
                .then(function (response) {
                    commit('setDownloadUrl', response.data)
                    commit('loadDownloadUrl', false)
                })
                .catch(function (error) {
                    store.dispatch('errorNotification', { msg: 'No se puede obtener direccion de descarga', error: error })
                    commit('loadDownloadUrl', false)
                    console.log(error)
                })
        },
        toggleDownloadFilesStatus({ commit }, payload) {
            commit('toggleDownloadFilesStatus', payload)
        },
        pullWindRoseData({ commit }, payload) {
            const url = `${uri}/report/windrose/${payload.deviceId}?day=${payload.date}`

            if (payload.deviceId === undefined)
                return

            axios.get(url, { headers: authHeader() })
                .then(function (response) {
                    commit('setWindRose', response.data)
                })
                .catch(function (error) {
                    store.dispatch('errorNotification', { msg: 'No se pueden cargar datos', error: error })
                    console.log(error)
                })
        },
        pullClassificationEvents({ commit }, payload) {
            const url = `${uri}/tmp/classificationEvents/${payload.deviceId}/view?day=${payload.date}`

            if (payload.deviceId === undefined)
                return

            axios.get(url, { headers: authHeader() })
                .then(function (response) {
                    commit('setClassificationEvents', response.data)
                })
                .catch(function (error) {
                    store.dispatch('errorNotification', { msg: 'No se pueden cargar datos', error: error })
                    console.log(error)
                })
        },
        updateDevice({ commit }, payload) {
            const url = `${uri}/device/${payload.deviceId}`

            const update = {
                address: payload.address,
                location: payload.location,
                name: payload.name,
                view: payload.view,
                laeqFilter: payload.laeqFilter,
                info: payload.info,
            }

            axios.put(url, update, { headers: authHeader() })
                .then(function (response) {
                    commit('updateDevice', payload)
                    commit('setNotification', {
                        view: true,
                        text: 'Dispositivo actualizado',
                        color: 'success'
                    })
                })
                .catch(function (error) {
                    store.dispatch('errorNotification', { msg: 'No se pudo eliminar dipositivo', error: error })
                    console.log(error)
                })
        },
        updateOptionsDevices({ commit }, payload) {
            const { deviceId } = payload

            const url = `${uri}/device/${deviceId}/options`

            const update = {
                autoYaxis: payload.autoYaxis,
                yaxisValue: payload.yaxisValue,
                trendYaxisValue: payload.trendYaxisValue,
                autoTrendYaxis: payload.autoTrendYaxis
            }

            axios.put(url, update, { headers: authHeader() })
                .then(function (response) {
                    commit('updateOptionsDevices', response.data)
                })
                .catch(function (error) {
                    store.dispatch('errorNotification', { msg: 'No se pudo actualizar las opciones', error: error })
                    console.log(error)
                })
        },
        removeDevice({ commit }, payload) {
            const url = `${uri}/device/${payload.deviceId}`

            axios.delete(url, { headers: authHeader() })
                .then(function (response) {
                    commit('removeDevice', payload.deviceId)
                })
                .catch(function (error) {
                    store.dispatch('errorNotification', { msg: 'No se pudo eliminar dipositivo', error: error })
                    console.log(error)
                })
        },
        updateSensor({ commit }, payload) {
            const url = `${uri}/device/${payload.deviceId}/sensor/${payload.sensorName}`

            const update = {
                description: payload.description,
                hasEvents: payload.hasEvents,
                highThreshold: payload.highThreshold,
                lowThreshold: payload.lowThreshold,
                offset: payload.offset,
                unit: payload.unit,
                viewUser: payload.viewUser,
                complianceLimits: payload.complianceLimits
            }

            axios.put(url, update, { headers: authHeader() })
                .then(function (response) {
                    commit('updateSensor', payload)
                    commit('setNotification', {
                        view: true,
                        text: 'Sensor actualizado',
                        color: 'success'
                    })
                })
                .catch(function (error) {
                    store.dispatch('errorNotification', { msg: 'No se pudo eliminar dipositivo', error: error })
                    console.log(error)
                })
        },
        handleLoading({ commit }, payload) {
            commit('setLoading', payload)
        },
        customNotification({ commit }, { text, color }) {
            commit('setNotification', {
                view: true,
                text: text,
                color: color
            })
        },
        errorNotification({ commit }, payload) {
            const error = payload.error.response
            let needAuth = true

            if (Object.hasOwnProperty.call(payload, 'needAuth'))
                needAuth = payload.needAuth

            if ((error.status === 403 || error.status === 401) && needAuth) {
                store.dispatch('logout')
                router.push('/login')
                return
            }

            commit('setNotification', {
                view: true,
                text: payload.msg,
                color: 'error'
            })
        },
        closeNotification({ commit }) {
            commit('setNotification', {
                view: false,
                text: '',
                color: 'success'
            })
        },
        viewDate({ commit }, payload) {
            commit('setViewDate', payload)
        },
        viewRangeDate({ commit }, payload) {
            commit('setViewRangeDate', payload)
        },
        pullOperation({ commit }, payload) {
            const url = `${uri}/operation?date=${payload.date}`

            return new Promise((resolve, reject) => {
                axios.get(url, { headers: authHeader() })
                    .then((response) => {
                        commit('setOperation', response.data)
                        resolve(response)
                    })
                    .catch((error) => {
                        store.dispatch('errorNotification', { msg: 'No se pueden cargar datos', error: error })
                        console.log(error)
                        reject(error)
                    })
            })

            axios.get(url, { headers: authHeader() })
                .then((response) => {
                    commit('setOperation', response.data)
                })
                .catch((error) => {
                    store.dispatch('errorNotification', { msg: 'No se pueden cargar datos', error: error })
                    console.log(error)
                })
        },
        pushOperation({ commit }, payload) {
            const url = `${uri}/operation`
            let activity = []

            payload.activity.forEach(e => {
                activity.push(e.value)
            })

            const operation = {
                date: payload.date,
                leader: payload.leader,
                workshift: payload.workshift,
                start: payload.start,
                end: payload.end,
                activity: activity,
            }

            axios.post(url, operation, { headers: authHeader() })
                .then((response) => {
                    commit('idOperation', response.data._id)
                    commit('setNotification', {
                        view: true,
                        text: 'Operacion agregada',
                        color: 'success'
                    })
                })
                .catch((error) => {
                    store.dispatch('errorNotification', { msg: 'No se pudo eliminar dipositivo', error: error })
                    console.log(error)
                })
        },
        updateOperation({ commit }, payload) {
            const url = `${uri}/operation`

            const update = {
                leader: payload.leader,
                workshift: payload.workshift,
                start: payload.start,
                end: payload.end,
                activity: payload.activity,
            }

            axios.put(url, update, { headers: authHeader() })
                .then((response) => {
                    commit('setNotification', {
                        view: true,
                        text: 'Dispositivo actualizado',
                        color: 'success'
                    })
                })
                .catch((error) => {
                    store.dispatch('errorNotification', { msg: 'No se pudo eliminar dipositivo', error: error })
                    console.log(error)
                })
        },
        removeOperation({ commit }, payload) {
            const url = `${uri}/operation?id=${payload.id}`

            axios.delete(url, { headers: authHeader() })
                .then((response) => {

                })
                .catch((error) => {
                    store.dispatch('errorNotification', { msg: 'No se pudo eliminar operacion', error: error })
                    console.log(error)
                })
        },
        pullAnnotation({ commit }, payload) {
            let url = `${uri}/annotation/${payload.deviceId}?day=${payload.date}`

            if (payload.endDate)
                url += `&endDate=${payload.endDate}`

            axios.get(url, { headers: authHeader() })
                .then((response) => {
                    commit('setAnnotation', response.data)
                })
                .catch((error) => {
                    store.dispatch('errorNotification', { msg: 'No se pudo cargar etiquetas', error: error })
                    console.log(error)
                })
        },
        pushAnnotation({ commit }, payload) {
            const url = `${uri}/annotation`

            const annotation = {
                deviceId: payload.deviceId,
                text: payload.text,
                dayReport: payload.dayReport,
                isTag: payload.isTag,
                tagId: payload.tagId,
                x: payload.x,
                x2: payload.x2
            }

            return new Promise((resolve, reject) => {
                axios.post(url, annotation, { headers: authHeader() })
                    .then((response) => {
                        resolve(response)
                    })
                    .catch((error) => {
                        store.dispatch('errorNotification', { msg: 'No se pudo guardar anotacion', error: error })
                        console.log(error)
                        reject(error)
                    })
            })
        },
        removeAnnotation({ commit }, payload) {
            const url = `${uri}/annotation?id=${payload.id}`

            axios.delete(url, { headers: authHeader() })
                .then((response) => {

                })
                .catch((error) => {
                    store.dispatch('errorNotification', { msg: 'No se pudo eliminar anotacion', error: error })
                    console.log(error)
                })
        },
        pullAnnotationTag({ commit }) {
            const url = `${uri}/annotation/tag`

            axios.get(url, { headers: authHeader() })
                .then((response) => {
                    commit('setAnnotationTag', response.data)
                })
                .catch((error) => {
                    store.dispatch('errorNotification', { msg: 'No se pudo cargar etiquetas', error: error })
                    console.log(error)
                })
        },
        pushAnnotationTag({ commit }, payload) {
            const url = `${uri}/annotation/tag`

            const tag = {
                text: payload.text,
                color: payload.color
            }

            axios.post(url, tag, { headers: authHeader() })
                .then((response) => {
                    commit('addAnnotationTag', response.data)
                })
                .catch((error) => {
                    store.dispatch('errorNotification', { msg: 'No se pudo guardar etiqueta ', error: error })
                    console.log(error)
                })
        },
        removeAnnotationTag({ commit }, payload) {
            const url = `${uri}/annotation/tag?id=${payload.id}`

            axios.delete(url, { headers: authHeader() })
                .then((response) => {
                    commit('cleanAnnotationTag', payload)
                })
                .catch((error) => {
                    store.dispatch('errorNotification', { msg: 'No se pudo eliminar operacion', error: error })
                    console.log(error)
                })
        },

        async updateAnnotationTag({ commit }, payload) {
            const url = `${uri}/annotation/tag?id=${payload.id}`

            try {
                const response = await axios.put(url, payload, { headers: authHeader() })

                commit('cleanAnnotationTag', payload)
                commit('addAnnotationTag', response.data)
            } catch (error) {
                return error
            }
        },

        pullProjects({ commit }) {
            const url = `${uri}/project`

            axios.get(url, { headers: authHeader() })
                .then((response) => {
                    commit('setProjects', response.data)

                    if (response.data.length > 0)
                        commit('setProject', response.data[0].projectId)
                })
                .catch((error) => {
                    store.dispatch('errorNotification', { msg: 'No se pudo cargar Redes continuas', error: error })
                    console.log(error)
                })
        },
        createProject({ commit }, payload) {
            const url = `${uri}/project/create`
            const project = {
                name: payload.name,
                devices: payload.devices
            }

            axios.post(url, project, { headers: authHeader() })
                .then((response) => {
                    commit('addProject', response.data)
                    store.dispatch('customNotification', { text: 'Red continua creado con exito', color: 'success' })
                })
                .catch((error) => {
                    store.dispatch('errorNotification', { msg: 'No se pudo crear Red continua', error: error })
                    console.log(error)
                })
        },

        pullDeviceDateResumeReport({ commit }, payload) {
            let { devices, loading } = payload
            let requests = []

            for (let i = 0; i < devices.length; i++) {
                let url = `${uri}/monthly/${devices[i]}/laeq_001?from=${payload.from}`
                if (payload.to)
                    url += `&to=${payload.to}`

                if (payload.cleanCahe)
                    url += '&cleancache=true'

                requests.push(axios.get(url, { headers: authHeader() }))
            }

            commit('loadingReportData', loading)
            let reports = []

            axios.all(requests)
                .then(axios.spread((...responses) => {
                    for (let i = 0; i < responses.length; i++) {
                        reports.push({
                            deviceId: devices[i],
                            info: responses[i].data
                        })
                    }

                    commit('setDeviceDateResumeReport', reports)
                    commit('loadingReportData', false)
                }))
                .catch(function (error) {
                    store.dispatch('errorNotification', { msg: 'No se pueden cargar datos', error: error })
                    console.log(error)
                })
        },

        async pullProjectTest({ commit }) {
            const url = `${uri}/projecttest`

            try {
                const response = await axios.get(url, { headers: authHeader() })
                commit('setProjectTest', response.data.data)
                return response.data.data
            } catch (error) {
                return error
            }
        },

        async setViewProjectTest({ commit }, payload) {
            commit('setViewProjectTest', payload)
        },

        async createProjectTest({ commit }, payload) {
            const url = `${uri}/projecttest`
            const { name, description, sensors } = payload
            const project = { name, description, sensors }

            try {
                const response = await axios.post(url, project, { headers: authHeader() })
                commit('addProjectTest', response.data.data)
                return response.data.data
            } catch (error) {
                return error
            }
        },

        async addSensorProjectTest({ commit }, payload) {
            const { sensors, projectID } = payload
            const url = `${uri}/projecttest/${projectID}/sensor`

            try {
                const response = await axios.patch(url, sensors, { headers: authHeader() })
                commit('editProjectTest', response.data.data)
                return response.data.data
            } catch (error) {
                return error
            }
        },

        async addReportsProjectTest(_, payload) {
            const url = `${uri}/projecttest/report`

            try {
                const response = await axios.post(url, payload, { headers: authHeader() })
                cleanTempUploadData()
                return response.data.data
            } catch (error) {
                return error
            }
        },

        async pullRangeTrend({ commit }) {
            const url = `${uri}/trend/range`

            try {
                const response = await axios.get(url, { headers: authHeader() })
                commit('setRangeTrend', response.data.data)
                return response.data.data
            } catch (error) {
                return error
            }
        },

        saveDashboardData({ commit }, payload) {
            commit('setDashboardData', payload)
        },

        async pullDashboardWeeklyAverage({ commit, getters }, payload) {
            if (!payload || !payload.deviceId) return null

            const deviceId = payload.deviceId
            const sensorName = payload.sensorName || 'laeq_001'
            if (!payload.referenceTs || !moment(payload.referenceTs).isValid()) {
                return getters.dashboardWeeklyAverage(deviceId)
            }

            const referenceMoment = moment(payload.referenceTs)
            const hourMinute = referenceMoment.format('HH:mm')
            const requestKey = `${sensorName}-${referenceMoment.format('YYYY-MM-DD HH:mm')}`
            const currentEntry = getters.dashboardWeeklyAverage(deviceId)

            if (currentEntry && currentEntry.requestKey === requestKey) {
                if (currentEntry.status === 'loading' || currentEntry.status === 'ready') {
                    return currentEntry
                }
            }

            commit('setDashboardWeeklyAverage', {
                deviceId,
                status: 'loading',
                value: null,
                hourMinute,
                sensorName,
                referenceTs: referenceMoment.toISOString(),
                updatedAt: currentEntry ? currentEntry.updatedAt : null,
                requestKey
            })

            const sensor = getters.sensorsByDeviceIdAndSensorName(deviceId, sensorName)
            const offset = sensor && sensor.offset !== null && sensor.offset !== undefined && !Number.isNaN(Number(sensor.offset))
                ? Number(sensor.offset)
                : 0

            const days = []
            for (let i = 0; i < 7; i += 1) {
                days.push(moment().subtract(i, 'days').format('YYYY-MM-DD'))
            }

            try {
                const requests = days.map(day => {
                    const url = `${uri}/report/${deviceId}/${sensorName}?day=${day}`
                    return axios.get(url, { headers: authHeader() }).catch(() => null)
                })

                const responses = await Promise.all(requests)
                const records = responses
                    .filter(response => response && response.data && Array.isArray(response.data))
                    .reduce((acc, response) => acc.concat(response.data), [])

                const minuteValues = []
                for (let i = 0; i < records.length; i += 1) {
                    const rec = records[i]
                    if (!rec || !rec.ts || !rec.report || rec.report.avg === undefined || rec.report.avg === null) continue

                    const ts = moment(rec.ts)
                    if (!ts.isValid()) continue
                    if (ts.hour() !== referenceMoment.hour() || ts.minute() !== referenceMoment.minute()) continue

                    minuteValues.push(Number(rec.report.avg) + offset)
                }

                const value = minuteValues.length > 0
                    ? minuteValues.reduce((acc, current) => acc + current, 0) / minuteValues.length
                    : null

                const entry = {
                    deviceId,
                    status: 'ready',
                    value,
                    hourMinute,
                    sensorName,
                    referenceTs: referenceMoment.toISOString(),
                    updatedAt: moment().toISOString(),
                    requestKey
                }

                commit('setDashboardWeeklyAverage', entry)
                return entry
            } catch (error) {
                commit('setDashboardWeeklyAverage', {
                    deviceId,
                    status: 'error',
                    value: null,
                    hourMinute,
                    sensorName,
                    referenceTs: referenceMoment.toISOString(),
                    updatedAt: moment().toISOString(),
                    requestKey
                })
                return null
            }
        },

        async pullDashboardWeeklyAverages({ dispatch }, devicesPayload) {
            if (!Array.isArray(devicesPayload) || devicesPayload.length === 0) return []

            return Promise.all(
                devicesPayload
                    .filter(device => device && device.deviceId)
                    .map(device => dispatch('pullDashboardWeeklyAverage', device))
            )
        },

        setDashboardCarouselRuntime({ commit }, payload) {
            commit('setDashboardCarouselRuntime', payload || {})
        },

        sendDashboardCarouselCommand({ commit }, payload) {
            if (!payload || !payload.command) return
            commit('emitDashboardCarouselCommand', payload)
        },

        async getDeviceDailyTrends(_, { deviceId, sensorName }) {
            const today = moment().format('YYYY-MM-DD')
            const url = `${uri}/report/trend/${deviceId}/${sensorName}?startDate=${today}&endDate=${today}`

            try {
                const response = await axios.get(url, { headers: authHeader() })
                // FIX: La respuesta viene como objeto { data: [...] }, no array directo
                const data = response.data.data || response.data

                if (data && Array.isArray(data) && data.length > 0) {
                    // Retornamos el array completo para que lo use quien lo necesite (Barra o Gráfico)
                    return data
                }
                return []
            } catch (error) {
                return []
            }
        },
    },

    getters: {
        drawer(state) {
            return state.drawer
        },
        loggedIn(state) {
            return state.status.loggedIn
        },
        user(state) {
            return state.user
        },
        uri() {
            return uri
        },
        loadLogin(state) {
            return state.loadLogin
        },
        loadRegister(state) {
            return state.loadRegister
        },
        errorLogin(state) {
            return state.errorLogin
        },
        errorRegister(state) {
            return state.errorRegister
        },
        errorRecover(state) {
            return state.errorRecover
        },
        successRegister(state) {
            return state.successRegister
        },
        successRecover(state) {
            return state.successRecover
        },
        isLoading(state) {
            return state.isLoading
        },
        viewDate(state) {
            return state.viewDate
        },
        viewRangeDate(state) {
            return state.viewRangeDate
        },
        snackbar(state) {
            return state.snackbar

        },
        devices(state) {
            return state.devices
        },
        device: (state, getters) => (id) => {
            const findDevice = getters.devices.filter(gist => gist.deviceId === id)
            return findDevice.length > 0 ? findDevice[0] : undefined
        },
        optionsDevices(state) {
            return state.optionsDevices
        },
        devicesInfo(state) {
            return state.devicesInfo
        },
        devicesLastReport(state) {
            return state.devicesLastReport
        },
        deviceInfo: (state, getters) => (id) => {
            if (!getters.devicesInfo || !Array.isArray(getters.devicesInfo)) {
                return null
            }
            return getters.devicesInfo.filter(gist => gist.deviceId === id)[0]
        },
        devicesDateReport(state) {
            return state.devicesDateReport
        },
        devicesDateReportOriginal(state) {
            return state.devicesDateReportOriginal
        },
        devicesDateTrendReport(state) {
            return state.devicesDateTrendReport
        },
        deviceStatsTrend(state) {
            return state.deviceStatsTrend
        },
        loadingReportData(state) {
            return state.loadingReportData
        },
        devicesDateResumeReport(state) {
            return state.devicesDateResumeReport
        },
        devicesDateEvent(state) {
            return state.devicesDateEvent
        },
        devicesDateCompare(state) {
            return state.devicesDateCompare
        },
        devicesHasLaeq: (state, getters) => (id) => {
            const sensors = getters.devices.find(e => {
                return e.deviceId === id
            })

            if (sensors === undefined)
                return false

            const hasLaeq = (element) => element.sensorType === "laeq"
            return sensors.sensors.some(hasLaeq)
        },
        deviceHasMap(state) {
            return state.devices.some(e => {
                return Object.hasOwnProperty.call(e, "location")
            })
        },
        sensors(state) {
            return state.sensors
        },
        allSensors(state) {
            return state.allSensors
        },

        sensorsByDeviceIdAndSensorName: (state, getters) => (deviceId, sensorName) => {
            const findSensor = getters.allSensors.filter(e => e.deviceId === deviceId && e.sensorName === sensorName)
            
            if (findSensor.length > 0) {
              return findSensor[0]
            } else {
              return null
            }
        },

        sensorsByDeviceId: (state, getters) => (deviceId) => {
            return getters.allSensors.filter(e => e.deviceId === deviceId)
        },

        sensor: (state, getters) => (device, sensor) => {
            return getters.sensors.find(e => e.deviceId === device && e.sensorName === sensor)
        },
        downloadFiles(state) {
            return state.downloadFiles
        },
        loadDownloadFiles(state) {
            return state.loadDownloadFiles
        },
        downloadUrl(state) {
            return state.downloadUrl.url
        },
        loadDownloadUrl(state) {
            return state.loadDownloadUrl
        },
        windRose(state) {
            return state.windRose
        },
        classificationEvents(state) {
            return state.classificationEvents
        },
        operation(state) {
            return state.operation
        },
        idOperation(state) {
            return state.idOperation
        },
        annotationTag(state) {
            return state.annotationTag
        },
        annotation(state) {
            return state.annotation
        },
        projects(state) {
            return state.projects
        },
        project(state) {
            return state.projectView
        },
        models(state) {
            return state.models
        },
        projectTest(state) {
            return state.projectTest
        },
        viewProjectTest(state) {
            return state.viewProjectTest
        },
        reportReferences: (state) => (deviceId) => {
            return state.reportReferences[deviceId]
        },
        dashBoardDevices(state) {
            return state.dashBoardDevices
        },
        dashboardWeeklyAverages(state) {
            return state.dashboardWeeklyAverages
        },
        dashboardWeeklyAverage: (state) => (deviceId) => {
            if (!deviceId) return null
            return state.dashboardWeeklyAverages[deviceId] || null
        },
        dashboardWeeklyAverageStatus: (state, getters) => (deviceId) => {
            const entry = getters.dashboardWeeklyAverage(deviceId)
            return entry ? entry.status : 'idle'
        },
        dashboardCarouselRuntime(state) {
            return state.dashboardCarouselRuntime
        },
        dashboardCarouselCommand(state) {
            return state.dashboardCarouselCommand
        },
        rangeReportData(state) {
            return state.rangeReportData
        },
        rangeTrend(state) {
            return state.rangeTrend
        }
    }
})

function cleanTempUploadData() {
    let storaged = JSON.parse(localStorage.getItem('continuum_tmp_upload'))
    storaged.splice(0, 10)
    localStorage.setItem('continuum_tmp_upload', JSON.stringify(storaged))
}
